import { Avatar, AvatarText } from "@amzn/stencil-react-components/avatar";
import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AmazonianInformation } from "lib/models/oui-models";
import React, { FC } from "react";
import { useCheckoutFormActions, useCheckoutFormItems } from "view/Page/CheckoutPage/useCheckoutForm";

import { FormField } from "./Helpers";

export interface CheckoutFormPanelProps {
  isLoadingUserInfo: boolean;
  isDeviceReturnRequired: boolean;
  userInfo: AmazonianInformation | undefined;
  checkoutItems: useCheckoutFormItems;
  checkoutActions: useCheckoutFormActions;
}

const STRING_PATH = "checkout.formPanel.managerCard";

const TitleSection: FC = () => (
  <Col gridGap="S300">
    <Text fontSize="T400" fontWeight="bold" color="neutral90">
      {getString(`${STRING_PATH}.step1`)}
    </Text>
    <Text fontSize="T200">{getString(`${STRING_PATH}.description`)}</Text>
  </Col>
);

interface ManagerInfoProps {
  manager: AmazonianInformation["manager"];
}

const ManagerInfo: FC<ManagerInfoProps> = ({ manager }) => {
  const getFullName = () => `${manager.basicInfo?.firstName || ""} ${manager.basicInfo?.lastName || ""}`.trim();

  return (
    <Col gridGap="S300">
      <Text fontSize="T300" fontWeight="bold" color="neutral90">
        {getString(`${STRING_PATH}.yourManager`)}
      </Text>
      {manager.basicInfo && (
        <Avatar
          fullName={getFullName()}
          username={manager.username || ""}
          profileImageUrl={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${manager.username}`}
        >
          {manager.jobInfo && (
            <AvatarText>{`L${manager.jobInfo.jobLevel || ""}, ${manager.jobInfo.businessTitle || ""}`}</AvatarText>
          )}
        </Avatar>
      )}
    </Col>
  );
};

const GetManagerApprovalCard: FC<CheckoutFormPanelProps> = (props: CheckoutFormPanelProps) => {
  return (
    <Card data-testid="manager-approval-card" padding="S300" width="100%">
      <Col gridGap="S300" width="100%">
        {/* Title */}
        <TitleSection />

        {/* Manager info */}
        {props.isLoadingUserInfo ? (
          <Spinner />
        ) : (
          props.userInfo?.manager && <ManagerInfo manager={props.userInfo?.manager} />
        )}

        {/* Business justification */}
        <FormField
          label={getString(`${STRING_PATH}.businessJustification`)}
          value={props.checkoutItems.formValues.businessJustification}
          onChange={(value) => props.checkoutActions.handleInputChange("businessJustification", value)}
          placeholder={getString(`${STRING_PATH}.placeholderBJ`)}
          testId="business-justification-input"
          isTextArea
        />

        {/* Returned devices */}
        {props.isDeviceReturnRequired && (
          <FormField
            label={getString(`${STRING_PATH}.serialNumberForReturnDevice`)}
            value={props.checkoutItems.formValues.serialNumberForReturnDevice}
            onChange={(value) => props.checkoutActions.handleInputChange("serialNumberForReturnDevice", value)}
            placeholder={getString(`${STRING_PATH}.placeHolderSerialNumbersForReturnDevices`)}
            tooltipText={getString(`${STRING_PATH}.tooltipSerialNumbersForReturnDevices`)}
            testId="return-devices-input"
            isTextArea
          />
        )}

        {/* Cost center */}
        <FormField
          label={getString(`${STRING_PATH}.costCenter`)}
          value={props.checkoutItems.formValues.costCenter}
          onChange={(value) => props.checkoutActions.handleInputChange("costCenter", value)}
          placeholder={getString(`${STRING_PATH}.placeholderCostCenter`)}
          chipText={getString(`${STRING_PATH}.askYourManagerCostCenter`)}
          testId="cost-center-input"
        />

        {/* Car CODE */}
        <FormField
          label={getString(`${STRING_PATH}.carCode`)}
          value={props.checkoutItems.formValues.carCode}
          onChange={(value) => props.checkoutActions.handleInputChange("carCode", value)}
          placeholder={getString(`${STRING_PATH}.placeholderCarCode`)}
          chipText={getString(`${STRING_PATH}.askYourManagerCarCode`)}
          testId="car-code-input"
        />

        {/* Checkbox */}
        <Row gridGap="S200">
          <Checkbox
            data-testid="manager-confirmation-checkbox"
            checked={props.checkoutItems.formValues.managerConfirmationCheckbox}
            onChange={(event) =>
              props.checkoutActions.handleInputChange("managerConfirmationCheckbox", event.target.checked)
            }
          />
          <Label color="neutral90">{getString(`${STRING_PATH}.managerConfirmation`)}</Label>
        </Row>
      </Col>
    </Card>
  );
};

export default GetManagerApprovalCard;
